<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import Panel from 'primevue/panel';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import { computed } from 'vue';
import Button from 'primevue/button';
import { LinkItem } from '@/utils/types';

const props = withDefaults(
  defineProps<{
    links: LinkItem[];
  }>(),
  {
    links: () => [],
  }
);

const typeLabelMap: Record<string, string> = {
  material: 'Materiały',
  presentation: 'Prezentacje',
  calculator: 'Kalkulatory',
};

const tabs = computed(() => {
  const groups = new Map<string, { title: string; value: string }>();
  for (const link of props.links) {
    const type = (link.type ?? 'inne').toString();
    if (!groups.has(type)) {
      const title = typeLabelMap[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
      groups.set(type, { title, value: type });
    }
  }
  return Array.from(groups.values());
});

const linksByType = computed(() => {
  const map: Record<string, LinkItem[]> = {};
  for (const link of props.links) {
    const type = (link.type ?? 'inne').toString();
    if (!map[type]) map[type] = [];
    map[type].push(link);
  }
  return map;
});

function onOpen(link: LinkItem) {
  if (!link?.url) return;
  if (link.open_in_new_tab) {
    window.open(link.url, '_blank', 'noopener');
  } else {
    window.location.href = link.url;
  }
}
</script>

<template>
  <div class="card pb-2">
    <Panel header="Linki i materiały" toggleable :collapsed="true">
      <Tabs :value="tabs[0]?.value ?? ''">
        <TabList>
          <Tab
            v-for="tab in tabs"
            :key="tab.value"
            :value="tab.value"
            class="text-sm font-semibold"
            >{{ tab.title }}</Tab
          >
        </TabList>
        <TabPanels class="p-0">
          <TabPanel v-for="tab in tabs" :key="tab.value" :value="tab.value">
            <ul class="m-0 p-0 list-none flex flex-wrap gap-2">
              <li v-for="link in linksByType[tab.value]" :key="link.id" class="flex items-center gap-2">
                <Button
                  class="font-semibold bg-white"
                  severity="secondary"
                  outlined
                  size="small"
                  @click="onOpen(link)"
                >
                  <i v-if="link.icon" :class="`pi ${link.icon}`" aria-hidden="true"></i>
                  <span class="ml-1">{{ link.title }}</span>
                </Button>
              </li>
            </ul>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Panel>
  </div>
</template>

<style scoped>
:deep(.p-panel) {
  background-color: var(--gray-50);
}

:deep(.p-panel .p-panel-content) {
  background-color: var(--gray-50);
}

:deep(.p-panel .p-panel-header) {
  background-color: var(--gray-50);
}

:deep(.p-panel .p-tabs) {
  background-color: var(--gray-50);
}

:deep(.p-panel .p-tabs .p-tabpanels) {
  padding: 1rem 0;
  background-color: var(--gray-50);
}

:deep(.p-panel .p-tabs .p-tablist-tab-list) {
  background-color: var(--gray-50);
}
</style>
